import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../lib/i18n";

const LOGO_URL = "/assets/logo.png";
const EMBLEM_URL = "/assets/emblem-v5.jpg";

export const Header = () => {
  const { lang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `font-sans-body text-[12px] tracking-[0.28em] uppercase font-semibold transition-colors ${
      isActive ? "text-[#C5A059]" : "text-white/90 hover:text-[#C5A059]"
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b-2 border-[#C5A059]/30 shadow-2xl" data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-28 lg:h-32">
        {/* Unified brand block: circular logo + IMPECLAND wordmark + Paraguay emblem */}
        <Link
          to="/"
          className="flex items-center gap-4 lg:gap-5 group"
          data-testid="header-logo"
        >
          {/* Circular ImpecLand logo */}
          <img
            src={LOGO_URL}
            alt="ImpecLand"
            className="h-16 w-16 lg:h-20 lg:w-20 object-cover rounded-full ring-2 ring-[#C5A059]/60 group-hover:ring-[#C5A059] transition-all shrink-0"
          />

          {/* IMPECLAND wordmark + tagline */}
          <div className="hidden sm:flex flex-col leading-none" translate="no">
            <span className="text-2xl lg:text-3xl font-bold tracking-[0.16em] whitespace-nowrap notranslate">
              <span className="text-white">IMPEC</span><span className="text-[#C5A059] -ml-[0.16em]">LAND</span>
            </span>
            <span className="text-[9px] lg:text-[10px] tracking-[0.32em] text-[#C5A059]/85 mt-2 uppercase font-medium">
              Land &amp; Real Estate Investments
            </span>
          </div>

          {/* Paraguay coat of arms — mirror counterpart of the circular logo */}
          <img
            src={EMBLEM_URL}
            alt="Republica del Paraguay"
            className="hidden md:block h-16 w-16 lg:h-20 lg:w-20 object-cover rounded-full ring-2 ring-[#C5A059]/60 group-hover:ring-[#C5A059] transition-all shrink-0"
            data-testid="paraguay-emblem"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7 lg:gap-10">
          <NavLink end to="/" className={linkClass} data-testid="nav-home">{t(lang, "nav_home")}</NavLink>
          <NavLink to="/gallery" className={linkClass} data-testid="nav-gallery">{t(lang, "nav_gallery")}</NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">{t(lang, "nav_about")}</NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">{t(lang, "nav_contact")}</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button className="md:hidden p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-button" aria-label="menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-black border-t border-[#C5A059]/20" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-5 gap-5">
            <NavLink end to="/" onClick={() => setMobileOpen(false)} className={linkClass}>{t(lang, "nav_home")}</NavLink>
            <NavLink to="/gallery" onClick={() => setMobileOpen(false)} className={linkClass}>{t(lang, "nav_gallery")}</NavLink>
            <NavLink to="/about" onClick={() => setMobileOpen(false)} className={linkClass}>{t(lang, "nav_about")}</NavLink>
            <NavLink to="/contact" onClick={() => setMobileOpen(false)} className={linkClass}>{t(lang, "nav_contact")}</NavLink>
          </nav>
        </div>
      )}
    </header>
  );
};
